import { IKnmAttachmentDownload } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { getServiceBaseURL } from 'APIPackage/MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from 'APIPackage/MainModule/enums/APIServiceEnum';
import { apiGet, apiGetBlob, apiPost } from 'APIPackage/MainModule/common/apiTools';

import { IKnmDocumentSignaturesUpload } from '../interfaces/IKnmDocumentSignaturesUpload';
import { IKnmAttachment } from '../interfaces/IKnmAttachment';

interface IFetchKnmDocumentAttachmentDownload {
  attachment: IKnmAttachmentDownload;
}

interface IFetchKnmDocumentAttachmentsDownloadZipSig {
  knmId: number;
  attachments: IKnmAttachmentDownload[];
}

export async function fetchKnmDocumentAttachmentDownload({
  attachment,
}: IFetchKnmDocumentAttachmentDownload): Promise<Blob> {
  const urlSearchParams = new URLSearchParams();

  Object.entries(attachment).forEach(([key, value]) => {
    if (value == null) return;
    urlSearchParams.append(key, String(value));
  });

  return apiGet<Blob>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/document/attachment?${urlSearchParams}`);
}

export async function fetchZipKnmAttachmentDownload(attachments: IKnmAttachmentDownload[]): Promise<Blob> {
  return apiPost<Blob>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/document/attachment/zip`,
    body: attachments,
  });
}

export async function fetchZipKnmAttachmentDownloadWithSig(
  data: IFetchKnmDocumentAttachmentsDownloadZipSig,
): Promise<Blob> {
  return apiPost<Blob>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/document/attachment/zip-with-sig`,
    body: data,
  });
}

interface IFetchKnmDocumentAttachmentUpload {
  file: File | Blob;
  fileName?: string;
}

export async function fetchKnmDocumentAttachmentUpload({
  file,
  fileName,
}: IFetchKnmDocumentAttachmentUpload): Promise<IKnmAttachment> {
  const formData = new FormData();
  const params = new URLSearchParams();

  formData.append('file', file);
  fileName && params.set('fileName', String(fileName));

  return apiPost<IKnmAttachment>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/document/attachment${params ? `?${params}` : ''}`,
    body: formData,
  });
}

export async function fetchPlanDocumentAttachmentUpload({
  file,
  fileName,
}: IFetchKnmDocumentAttachmentUpload): Promise<IKnmAttachment> {
  const formData = new FormData();
  const params = new URLSearchParams();

  formData.append('file', file);
  fileName && params.set('fileName', String(fileName));

  return apiPost<IKnmAttachment>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm-plan/document/attachment${params ? `?${params}` : ''}`,
    body: formData,
  });
}

export async function fetchKnmDocumentSignaturesUpload({
  document,
  signatures,
}: IKnmDocumentSignaturesUpload): Promise<IKnmAttachment> {
  const data = { document, signatures };

  return apiPost<IKnmAttachment>({
    url: `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/signature/verify/attachment-document-signatures`,
    body: data,
  });
}

export async function fetchKnmDocumentsByPlanIdDownload(planId: IKnmPlan['id']): Promise<IKnmAttachmentDownload[]> {
  return apiGet<IKnmAttachmentDownload[]>(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/document/attachment/web/${planId}`,
  );
}

export async function fetchKnmPlanDocumentAttachmentDownload({
  attachment,
}: IFetchKnmDocumentAttachmentDownload): Promise<Blob> {
  const urlSearchParams = new URLSearchParams();

  Object.keys(attachment).forEach((key: keyof IKnmAttachmentDownload) => {
    const value = String(attachment[key]);

    if (value) {
      urlSearchParams.append(key, value);
    }
  });

  return apiGetBlob(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm-plan/document/attachment?${urlSearchParams}`);
}

export async function fetchKnmSignatureFileDownload(filePath: string, bucket: string): Promise<Blob> {
  const urlSearchParams = new URLSearchParams();

  urlSearchParams.append('filePath', filePath);
  urlSearchParams.append('bucketName', bucket);

  return apiGet<Blob>(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/document/attachment/v2?${urlSearchParams}`,
  );
}
